// src/services/pipsWordPuzzleStorageService.ts
import { logger } from '../utils/logger';
// Database-only storage for Pip's Word Puzzle stats (no localStorage backup)

import { supabase } from './supabase';
import { GameStats, GameState } from '../components/games/pipsWordPuzzle/types';

const GAME_ID = 'pips_word_puzzle';

export interface PipsWordPuzzleScore {
  id: string;
  user_id: string;
  score: number;
  puzzle_word: string;
  puzzle_date: string;
  guesses_used: number;
  hints_used: number;
  won: boolean;
  created_at: string;
  // Joined from profiles
  username?: string;
  display_name?: string;
}

/**
 * Get user's Nerdle stats from database
 */
export async function getStats(): Promise<GameStats> {
  const { data: { user } } = await supabase.auth.getUser();

  if (!user) {
    // Not logged in - return default stats
    return getDefaultStats();
  }

  const { data, error } = await supabase
    .from('game_scores')
    .select('*')
    .eq('user_id', user.id)
    .eq('game_id', GAME_ID)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    logger.error('Error loading Nerdle stats:', error);
    return getDefaultStats();
  }

  if (!data) {
    // No stats yet - return default
    return getDefaultStats();
  }

  // Parse stats from game_data JSONB column
  return {
    gamesPlayed: data.game_data?.gamesPlayed || 0,
    gamesWon: data.game_data?.gamesWon || 0,
    currentStreak: data.game_data?.currentStreak || 0,
    maxStreak: data.game_data?.maxStreak || 0,
    points: data.score || 15,
    guessDistribution: data.game_data?.guessDistribution || [0, 0, 0, 0, 0, 0]
  };
}

/**
 * Save user's Nerdle stats to database
 */
export async function saveStats(stats: GameStats): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();

  if (!user) {
    logger.warn('Cannot save stats - user not logged in');
    return;
  }

  logger.log('💾 Saving stats for user:', user.id);

  // Get existing game_data to preserve all fields
  const { data: existingRecord, error: checkError } = await supabase
    .from('game_scores')
    .select('game_data')
    .eq('user_id', user.id)
    .eq('game_id', GAME_ID)
    .maybeSingle();

  if (checkError) {
    logger.error('Error checking existing record:', checkError);
    return;
  }

  logger.log('Existing record:', existingRecord ? 'found' : 'not found');

  // Preserve existing game_data and only update stats
  const existingGameData = existingRecord?.game_data || {};

  const recordData = {
    user_id: user.id,
    game_id: GAME_ID,
    score: stats.points,
    game_data: {
      ...existingGameData, // Preserve all existing fields
      gamesPlayed: stats.gamesPlayed,
      gamesWon: stats.gamesWon,
      currentStreak: stats.currentStreak,
      maxStreak: stats.maxStreak,
      guessDistribution: stats.guessDistribution,
      completedPuzzles: existingGameData.completedPuzzles || [], // Explicitly preserve completed puzzles
      lastUpdated: new Date().toISOString()
    }
  };

  logger.log('Record data:', recordData);

  let error;
  if (existingRecord) {
    // Update existing record
    logger.log('Attempting UPDATE...');
    const result = await supabase
      .from('game_scores')
      .update(recordData)
      .eq('user_id', user.id)
      .eq('game_id', GAME_ID);
    error = result.error;
    logger.log('UPDATE result:', result);
  } else {
    // Insert new record
    logger.log('Attempting INSERT...');
    const result = await supabase
      .from('game_scores')
      .insert(recordData);
    error = result.error;
    logger.log('INSERT result:', result);
  }

  if (error) {
    logger.error('❌ Error saving Nerdle stats:', error);
  } else {
    logger.log('✅ Stats saved successfully!');
  }
}

/**
 * Get current game state from database
 */
export async function getGameState(): Promise<GameState | null> {
  const { data: { user } } = await supabase.auth.getUser();

  if (!user) {
    return null;
  }

  const { data, error } = await supabase
    .from('game_scores')
    .select('game_data')
    .eq('user_id', user.id)
    .eq('game_id', GAME_ID)
    .maybeSingle();

  if (error || !data) {
    return null;
  }

  // Check if there's a saved game state
  const gameState = data.game_data?.currentGameState;
  if (!gameState) {
    return null;
  }

  return gameState;
}

/**
 * Save current game state to database
 */
export async function saveGameState(state: GameState): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();

  if (!user) {
    return;
  }

  // Get current stats first
  const stats = await getStats();

  // Get existing game_data to preserve all fields
  const { data: existingRecord } = await supabase
    .from('game_scores')
    .select('game_data')
    .eq('user_id', user.id)
    .eq('game_id', GAME_ID)
    .maybeSingle();

  // Preserve existing game_data and only update what's needed
  const existingGameData = existingRecord?.game_data || {};

  const recordData = {
    user_id: user.id,
    game_id: GAME_ID,
    score: stats.points,
    game_data: {
      ...existingGameData, // Preserve all existing fields
      gamesPlayed: stats.gamesPlayed,
      gamesWon: stats.gamesWon,
      currentStreak: stats.currentStreak,
      maxStreak: stats.maxStreak,
      guessDistribution: stats.guessDistribution,
      completedPuzzles: existingGameData.completedPuzzles || [], // Explicitly preserve completed puzzles
      lastUpdated: new Date().toISOString(),
      currentGameState: state // Store current game state
    }
  };

  let error;
  if (existingRecord) {
    // Update existing record
    const result = await supabase
      .from('game_scores')
      .update(recordData)
      .eq('user_id', user.id)
      .eq('game_id', GAME_ID);
    error = result.error;
  } else {
    // Insert new record
    const result = await supabase
      .from('game_scores')
      .insert(recordData);
    error = result.error;
  }

  if (error) {
    logger.error('Error saving game state:', error);
  }
}

/**
 * Clear current game state from database
 */
export async function clearGameState(): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();

  if (!user) {
    return;
  }

  // Get current stats
  const stats = await getStats();

  // Get existing game_data to preserve all fields
  const { data: existingRecord } = await supabase
    .from('game_scores')
    .select('game_data')
    .eq('user_id', user.id)
    .eq('game_id', GAME_ID)
    .maybeSingle();

  if (!existingRecord) {
    return; // Nothing to clear
  }

  // Preserve existing game_data and only clear game state
  const existingGameData = existingRecord.game_data || {};

  const recordData = {
    user_id: user.id,
    game_id: GAME_ID,
    score: stats.points,
    game_data: {
      ...existingGameData, // Preserve all existing fields
      gamesPlayed: stats.gamesPlayed,
      gamesWon: stats.gamesWon,
      currentStreak: stats.currentStreak,
      maxStreak: stats.maxStreak,
      guessDistribution: stats.guessDistribution,
      completedPuzzles: existingGameData.completedPuzzles || [], // Explicitly preserve completed puzzles
      lastUpdated: new Date().toISOString(),
      currentGameState: null // Clear game state
    }
  };

  // Update existing record
  const { error } = await supabase
    .from('game_scores')
    .update(recordData)
    .eq('user_id', user.id)
    .eq('game_id', GAME_ID);

  if (error) {
    logger.error('Error clearing game state:', error);
  }
}

/**
 * Update stats after a game
 */
export function updateStats(
  stats: GameStats,
  won: boolean,
  guessCount: number,
  pointsEarned: number
): GameStats {
  const newStats = { ...stats };

  newStats.gamesPlayed++;
  newStats.points += pointsEarned;

  if (won) {
    newStats.gamesWon++;
    newStats.currentStreak++;
    newStats.maxStreak = Math.max(newStats.maxStreak, newStats.currentStreak);
    newStats.guessDistribution[guessCount - 1]++;
  } else {
    newStats.currentStreak = 0;
  }

  return newStats;
}

/**
 * Clear all progress (for testing or account deletion)
 */
export async function clearAllProgress(): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();

  if (user) {
    // Delete from database
    await supabase
      .from('game_scores')
      .delete()
      .eq('user_id', user.id)
      .eq('game_id', GAME_ID);
  }
}

/**
 * Mark puzzle as completed in database
 */
export async function markPuzzleCompleted(puzzleDate: string, puzzleWord: string): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      logger.warn('Cannot mark puzzle as completed - user not logged in');
      return;
    }

    logger.log(`📝 Marking puzzle as completed: ${puzzleDate}:${puzzleWord}`);

    // Get current stats and completed puzzles
    const stats = await getStats();
    const { data: existingRecord, error: fetchError } = await supabase
      .from('game_scores')
      .select('game_data')
      .eq('user_id', user.id)
      .eq('game_id', GAME_ID)
      .maybeSingle();

    if (fetchError) {
      logger.error('Error fetching existing record for marking completed:', fetchError);
      return;
    }

    // Preserve existing game_data
    const existingGameData = existingRecord?.game_data || {};
    const currentCompleted = existingGameData.completedPuzzles || [];
    const key = `${puzzleDate}:${puzzleWord}`;

    if (currentCompleted.includes(key)) {
      logger.log(`✓ Puzzle already marked as completed: ${key}`);
      return;
    }

    currentCompleted.push(key);
    logger.log(`Adding to completed puzzles: ${key}`);
    logger.log(`Total completed puzzles: ${currentCompleted.length}`);

    const recordData = {
      user_id: user.id,
      game_id: GAME_ID,
      score: stats.points,
      game_data: {
        ...existingGameData, // Preserve all existing fields
        gamesPlayed: stats.gamesPlayed,
        gamesWon: stats.gamesWon,
        currentStreak: stats.currentStreak,
        maxStreak: stats.maxStreak,
        guessDistribution: stats.guessDistribution,
        lastUpdated: new Date().toISOString(),
        completedPuzzles: currentCompleted
      }
    };

    let error;
    if (existingRecord) {
      // Update existing record
      logger.log('Updating existing record with completed puzzle...');
      const result = await supabase
        .from('game_scores')
        .update(recordData)
        .eq('user_id', user.id)
        .eq('game_id', GAME_ID);
      error = result.error;
    } else {
      // Insert new record
      logger.log('Inserting new record with completed puzzle...');
      const result = await supabase
        .from('game_scores')
        .insert(recordData);
      error = result.error;
    }

    if (error) {
      logger.error('❌ Error marking puzzle as completed:', error);
    } else {
      logger.log('✅ Puzzle marked as completed successfully!');
    }
  } catch (error) {
    logger.error('❌ Exception in markPuzzleCompleted:', error);
  }
}

/**
 * Get completed puzzles from database
 */
export async function getCompletedPuzzles(): Promise<string[]> {
  const { data: { user } } = await supabase.auth.getUser();

  if (!user) {
    logger.log('No user logged in, returning empty completed puzzles array');
    return [];
  }

  const { data, error } = await supabase
    .from('game_scores')
    .select('game_data')
    .eq('user_id', user.id)
    .eq('game_id', GAME_ID)
    .maybeSingle();

  if (error) {
    logger.error('Error fetching completed puzzles:', error);
    return [];
  }

  if (!data) {
    logger.log('No game_scores record found, returning empty completed puzzles array');
    return [];
  }

  const completedPuzzles = data.game_data?.completedPuzzles || [];
  logger.log(`📋 Found ${completedPuzzles.length} completed puzzles:`, completedPuzzles);
  return completedPuzzles;
}

/**
 * Check if puzzle is completed
 */
export async function isPuzzleCompleted(puzzleDate: string, puzzleWord?: string): Promise<boolean> {
  const completed = await getCompletedPuzzles();

  if (puzzleWord) {
    const key = `${puzzleDate}:${puzzleWord}`;
    return completed.includes(key);
  }

  return completed.some(key => key.startsWith(`${puzzleDate}:`));
}

/**
 * Get default stats for new players
 */
function getDefaultStats(): GameStats {
  return {
    gamesPlayed: 0,
    gamesWon: 0,
    currentStreak: 0,
    maxStreak: 0,
    points: 15, // Start new players with 15 points so they can use hints
    guessDistribution: [0, 0, 0, 0, 0, 0]
  };
}

/**
 * Submit a Nerdle game score to the leaderboard
 */
export async function submitLeaderboardScore(
  puzzleWord: string,
  puzzleDate: string,
  guessesUsed: number,
  hintsUsed: number,
  won: boolean,
  score: number
): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      logger.error('No user logged in');
      return false;
    }

    const { error } = await supabase
      .from('pips_word_puzzle_scores')
      .insert({
        user_id: user.id,
        score: score,
        puzzle_word: puzzleWord,
        puzzle_date: puzzleDate,
        guesses_used: guessesUsed,
        hints_used: hintsUsed,
        won: won,
      });

    if (error) {
      logger.error('Error submitting Nerdle score to leaderboard:', error);
      return false;
    }

    return true;
  } catch (error) {
    logger.error('Error in submitLeaderboardScore:', error);
    return false;
  }
}

/**
 * Get top scores for Nerdle leaderboard
 */
export async function getLeaderboard(limit: number = 10): Promise<PipsWordPuzzleScore[]> {
  try {
    logger.log('🔍 Fetching Nerdle leaderboard with limit:', limit);

    // Fetch more scores than needed to ensure we get enough unique users
    const { data: scores, error: scoresError } = await supabase
      .from('pips_word_puzzle_scores')
      .select('id, user_id, score, puzzle_word, puzzle_date, guesses_used, hints_used, won, created_at')
      .order('score', { ascending: false })
      .limit(limit * 3); // Fetch 3x to account for duplicate users

    if (scoresError) {
      logger.error('❌ Error fetching Nerdle leaderboard scores:', scoresError);
      return [];
    }

    logger.log('✅ Nerdle leaderboard scores query successful');
    logger.log('📈 Number of scores:', scores?.length ?? 0);

    if (!scores || scores.length === 0) {
      logger.warn('⚠️ No Nerdle leaderboard data returned from query');
      return [];
    }

    // Deduplicate: keep only the highest score per user
    const seenUsers = new Set<string>();
    const uniqueScores = scores.filter(score => {
      if (seenUsers.has(score.user_id)) {
        return false;
      }
      seenUsers.add(score.user_id);
      return true;
    }).slice(0, limit); // Take only the top N unique users

    logger.log('📊 Unique users after deduplication:', uniqueScores.length);

    // Fetch profiles for all unique user IDs
    const userIds = uniqueScores.map(s => s.user_id);
    const { data: profiles, error: profilesError } = await supabase
      .from('profiles')
      .select('id, username, display_name')
      .in('id', userIds);

    if (profilesError) {
      logger.warn('⚠️ Error fetching profiles for Nerdle leaderboard:', profilesError);
      // Continue without profile data rather than failing completely
    }

    logger.log('✅ Profiles fetched:', profiles?.length ?? 0);

    // Create a map of user_id -> profile for quick lookup
    const profileMap = new Map(profiles?.map(p => [p.id, p]) ?? []);

    // Combine scores with profile data
    return uniqueScores.map(score => {
      const profile = profileMap.get(score.user_id);
      return {
        id: score.id,
        user_id: score.user_id,
        score: score.score,
        puzzle_word: score.puzzle_word,
        puzzle_date: score.puzzle_date,
        guesses_used: score.guesses_used,
        hints_used: score.hints_used,
        won: score.won,
        created_at: score.created_at,
        username: profile?.username,
        display_name: profile?.display_name,
      };
    });
  } catch (error) {
    logger.error('❌ Error in getLeaderboard:', error);
    return [];
  }
}

/**
 * Get user's personal best Nerdle score
 */
export async function getPersonalBest(): Promise<number> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return 0;

    const { data, error } = await supabase
      .from('pips_word_puzzle_scores')
      .select('score')
      .eq('user_id', user.id)
      .order('score', { ascending: false })
      .limit(1)
      .single();

    if (error && error.code !== 'PGRST116') {
      logger.error('Error getting Nerdle personal best:', error);
      return 0;
    }

    return data?.score ?? 0;
  } catch (error) {
    logger.error('Error in getPersonalBest:', error);
    return 0;
  }
}

/**
 * Get user's rank in the leaderboard
 * Returns 0 if user has no scores
 */
export async function getUserRank(): Promise<number> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return 0;

    // Get user's best score first
    const userBestScore = await getPersonalBest();
    if (userBestScore === 0) return 0;

    // Fetch all scores to calculate rank
    const { data: scores, error } = await supabase
      .from('pips_word_puzzle_scores')
      .select('user_id, score')
      .order('score', { ascending: false });

    if (error) {
      logger.error('Error fetching scores for rank calculation:', error);
      return 0;
    }

    if (!scores || scores.length === 0) return 0;

    // Deduplicate: keep only the highest score per user
    const seenUsers = new Set<string>();
    const uniqueScores = scores.filter(score => {
      if (seenUsers.has(score.user_id)) {
        return false;
      }
      seenUsers.add(score.user_id);
      return true;
    });

    // Find user's rank (1-indexed)
    const rank = uniqueScores.findIndex(score => score.user_id === user.id);
    return rank === -1 ? 0 : rank + 1;
  } catch (error) {
    logger.error('Error in getUserRank:', error);
    return 0;
  }
}

/**
 * One-time migration from localStorage to database (optional, for transitioning users)
 */
export async function migrateLocalStatsToDatabase(): Promise<boolean> {
  // This function is now a no-op since we're not using localStorage anymore
  // Keep it for backwards compatibility but it does nothing
  return false;
}